/**
 * @module permissions
 * @description
 * Este módulo define as permissões de acesso para diferentes funcionalidades do sistema TugasCoins.
 * Ele centraliza a gestão de permissões, garantindo consistência e facilitando a implementação
 * de controle de acesso baseado em papéis (RBAC) em toda a aplicação.
 *
 * As permissões são definidas como strings únicas e descritivas, mapeando diretamente
 * para ações específicas que podem ser realizadas sobre as entidades do sistema.
 * Além disso, são fornecidos tipos TypeScript para garantir a segurança e a clareza do código,
 * bem como um mapeamento de permissões por tipo de usuário para uma integração robusta com o RBAC.
 */

/**
 * Tipo que representa uma permissão de acesso no sistema TugasCoins.
 * Cada literal de string corresponde a uma ação específica permitida.
 * Este tipo é fundamental para a tipagem forte das permissões em toda a aplicação.
 */
export type Permissao =
  // Permissões para a entidade 'usuarios'
  | 'criar_usuario'
  | 'ler_usuario'
  | 'atualizar_usuario'
  | 'excluir_usuario'
  | 'gerenciar_permissoes_usuario'

  // Permissões para a entidade 'clientes'
  | 'criar_cliente'
  | 'ler_cliente'
  | 'atualizar_cliente'
  | 'excluir_cliente'

  // Permissões para a entidade 'unidades'
  | 'criar_unidade'
  | 'ler_unidade'
  | 'atualizar_unidade'
  | 'excluir_unidade'

  // Permissões para a entidade 'transacoes'
  | 'criar_transacao'
  | 'ler_transacao'
  | 'atualizar_transacao'
  | 'excluir_transacao'
  | 'aprovar_transacao'

  // Permissões para a entidade 'produtos'
  | 'criar_produto'
  | 'ler_produto'
  | 'atualizar_produto'
  | 'excluir_produto'

  // Permissões para a entidade 'categorias_produtos'
  | 'criar_categoria_produto'
  | 'ler_categoria_produto'
  | 'atualizar_categoria_produto'
  | 'excluir_categoria_produto'

  // Permissões para a entidade 'campanhas' e 'regras_campanhas'
  | 'criar_campanha'
  | 'ler_campanha'
  | 'atualizar_campanha'
  | 'excluir_campanha'
  | 'gerenciar_regras_campanha'

  // Permissões para a entidade 'niveis_fidelidade'
  | 'criar_nivel_fidelidade'
  | 'ler_nivel_fidelidade'
  | 'atualizar_nivel_fidelidade'
  | 'excluir_nivel_fidelidade'

  // Permissões para a entidade 'badges'
  | 'criar_badge'
  | 'ler_badge'
  | 'atualizar_badge'
  | 'excluir_badge'

  // Permissões para a entidade 'operadores_caixa' (se houver distinção do 'usuarios')
  | 'criar_operador_caixa'
  | 'ler_operador_caixa'
  | 'atualizar_operador_caixa'
  | 'excluir_operador_caixa'

  // Permissões para relatórios
  | 'gerar_relatorio_geral'
  | 'gerar_relatorio_financeiro'
  | 'gerar_relatorio_clientes'
  | 'gerar_relatorio_transacoes';

/**
 * Objeto constante que mapeia todas as permissões disponíveis no sistema TugasCoins.
 * Cada chave é um identificador de permissão e seu valor é a própria string da permissão.
 * Isso garante que todas as referências às permissões sejam consistentes e centralizadas.
 */
export const PERMISSOES = {
  // Permissões de Usuário (CRUD na tabela 'usuarios')
  criar_usuario: 'criar_usuario' as Permissao,
  ler_usuario: 'ler_usuario' as Permissao,
  atualizar_usuario: 'atualizar_usuario' as Permissao,
  excluir_usuario: 'excluir_usuario' as Permissao,
  gerenciar_permissoes_usuario: 'gerenciar_permissoes_usuario' as Permissao,

  // Permissões de Cliente (CRUD na tabela 'clientes')
  criar_cliente: 'criar_cliente' as Permissao,
  ler_cliente: 'ler_cliente' as Permissao,
  atualizar_cliente: 'atualizar_cliente' as Permissao,
  excluir_cliente: 'excluir_cliente' as Permissao,

  // Permissões de Unidade (CRUD na tabela 'unidades')
  criar_unidade: 'criar_unidade' as Permissao,
  ler_unidade: 'ler_unidade' as Permissao,
  atualizar_unidade: 'atualizar_unidade' as Permissao,
  excluir_unidade: 'excluir_unidade' as Permissao,

  // Permissões de Transação (CRUD na tabela 'transacoes')
  criar_transacao: 'criar_transacao' as Permissao,
  ler_transacao: 'ler_transacao' as Permissao,
  atualizar_transacao: 'atualizar_transacao' as Permissao,
  excluir_transacao: 'excluir_transacao' as Permissao,
  aprovar_transacao: 'aprovar_transacao' as Permissao,

  // Permissões de Produto (CRUD na tabela 'produtos')
  criar_produto: 'criar_produto' as Permissao,
  ler_produto: 'ler_produto' as Permissao,
  atualizar_produto: 'atualizar_produto' as Permissao,
  excluir_produto: 'excluir_produto' as Permissao,

  // Permissões de Categoria de Produto (CRUD na tabela 'categorias_produtos')
  criar_categoria_produto: 'criar_categoria_produto' as Permissao,
  ler_categoria_produto: 'ler_categoria_produto' as Permissao,
  atualizar_categoria_produto: 'atualizar_categoria_produto' as Permissao,
  excluir_categoria_produto: 'excluir_categoria_produto' as Permissao,

  // Permissões de Campanha (CRUD na tabela 'campanhas' e 'regras_campanhas')
  criar_campanha: 'criar_campanha' as Permissao,
  ler_campanha: 'ler_campanha' as Permissao,
  atualizar_campanha: 'atualizar_campanha' as Permissao,
  excluir_campanha: 'excluir_campanha' as Permissao,
  gerenciar_regras_campanha: 'gerenciar_regras_campanha' as Permissao,

  // Permissões de Níveis de Fidelidade (CRUD na tabela 'niveis_fidelidade')
  criar_nivel_fidelidade: 'criar_nivel_fidelidade' as Permissao,
  ler_nivel_fidelidade: 'ler_nivel_fidelidade' as Permissao,
  atualizar_nivel_fidelidade: 'atualizar_nivel_fidelidade' as Permissao,
  excluir_nivel_fidelidade: 'excluir_nivel_fidelidade' as Permissao,

  // Permissões de Badges (CRUD na tabela 'badges')
  criar_badge: 'criar_badge' as Permissao,
  ler_badge: 'ler_badge' as Permissao,
  atualizar_badge: 'atualizar_badge' as Permissao,
  excluir_badge: 'excluir_badge' as Permissao,

  // Permissões de Operadores de Caixa (se houver distinção do 'usuarios')
  criar_operador_caixa: 'criar_operador_caixa' as Permissao,
  ler_operador_caixa: 'ler_operador_caixa' as Permissao,
  atualizar_operador_caixa: 'atualizar_operador_caixa' as Permissao,
  excluir_operador_caixa: 'excluir_operador_caixa' as Permissao,

  // Permissões de Relatórios
  gerar_relatorio_geral: 'gerar_relatorio_geral' as Permissao,
  gerar_relatorio_financeiro: 'gerar_relatorio_financeiro' as Permissao,
  gerar_relatorio_clientes: 'gerar_relatorio_clientes' as Permissao,
  gerar_relatorio_transacoes: 'gerar_relatorio_transacoes' as Permissao,
};

/**
 * Tipo inferido para as chaves do objeto PERMISSOES, útil para validação
 * e para garantir que qualquer string usada como permissão seja uma chave válida.
 */
export type ChavePermissao = keyof typeof PERMISSOES;
